import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAlert } from '../../contexts/AlertContext';
import { deckApi } from '../../services/deckApi';
import { uploadProjectFile } from '../../mocks/workApi';
import { Cover, DeckLayout, DraftFile, DraftSlide, EditSlide, GuideMeta, LayoutSlot } from '../../types/deck';
import { Proposal } from '../../types/proposal';
import { usePdfPages } from '../../utils/pdfPages';

/**
 * 제안서 PPT 만들기(#132 · 2026-11-02) — GRK_admin 「템플릿 작성」(`pages/PptDraft.tsx`)과 같은 화면 · 흐름.
 *   ① 원고 · 설정 — 원고는 **제안서 칸**(글이 있는 것). 가이드 md(기본 최신판) · 표지 · 그림 · 분량
 *   ② 미리보기(PDF)에서 고치기 · 저장 — 내 컴퓨터(PPTX · PDF) · 드라이브 「02.제안서」(프로젝트만)
 * 본체는 서버 공용 grk-deck. 장 구성 · 레이아웃 · 글자 규칙은 grk-deck 의 GRK 가이드 md 가 정본이다 — 여기 다시 쓰지 않는다.
 * 고친 장 · 그림은 이 화면에만 있다(서버에 남지 않는다). 닫거나 새로고침하면 사라진다.
 */

const COVERS: { v: Cover; label: string }[] = [
  { v: 'G', label: 'G 컨설팅' },
  { v: 'R', label: 'R 연구' },
  { v: 'K', label: 'K 투자·전략' },
];
const SIZES = [10, 15, 25];
const POLL_MS = 4000;
const STEPS = ['원고 · 설정', '미리보기(수정) · 저장'] as const;
type Step = 1 | 2;
const CONFIRM = '[확인 필요]';

const safeName = (s: string) => s.replace(/[\\/:*?"<>|]/g, '_').trim();
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
};
const mmss = (ms: number) => {
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}초` : `${Math.floor(s / 60)}분 ${s % 60}초`;
};

/** 레이아웃을 바꿀 때 — 같은 역할 칸끼리 순서대로 옮긴다 */
function moveSlots(from: LayoutSlot[], to: LayoutSlot[], values: Record<string, string>): Record<string, string> {
  const queue = new Map<string, string[]>();
  for (const s of from) if (values[s.key]) queue.set(s.role, [...(queue.get(s.role) ?? []), values[s.key]]);
  const out: Record<string, string> = {};
  for (const s of to) {
    const v = queue.get(s.role)?.shift();
    if (v) out[s.key] = v;
  }
  return out;
}

const slideText = (s: EditSlide) => Object.values(s.texts).join(' ');
/** AI 가 원문에서 못 찾은 숫자 중 지금 글에 남은 것 */
const leftNumbers = (s: EditSlide) => s.unknownNumbers.filter((n) => slideText(s).includes(n));

type AiPhase = 'idle' | 'reading' | 'ai' | 'failed';

interface Props {
  projectId: number;
  projectName: string;
  proposal: Proposal | null;
  onClose: () => void;
}

export const ProposalDeck: React.FC<Props> = ({ projectId, projectName, proposal, onClose }) => {
  const { user } = useAuth();
  const { showConfirm, showToast } = useAlert();
  const [step, setStep] = useState<Step>(1);

  // ① 원고 · 설정 — 원고는 제안서 칸
  const sections = useMemo(
    () => (proposal?.sections ?? []).filter((s) => s.fill !== '절' && (s.content ?? '').trim()).sort((a, b) => a.sortOrder - b.sortOrder),
    [proposal],
  );
  const [images, setImages] = useState<File[]>([]);
  const [guides, setGuides] = useState<GuideMeta[]>([]);
  const [guide, setGuide] = useState('');
  const [layouts, setLayouts] = useState<Map<string, DeckLayout>>(new Map());
  const [title, setTitle] = useState(projectName);
  const [cover, setCover] = useState<Cover>('G');
  const [coverPhoto, setCoverPhoto] = useState('');
  const [size, setSize] = useState(15);
  const [phase, setPhase] = useState<AiPhase>('idle');
  const [error, setError] = useState('');
  const [read, setRead] = useState<DraftFile[] | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [aiMs, setAiMs] = useState<number | null>(null);
  const startedAt = useRef(0);
  const imgInput = useRef<HTMLInputElement>(null);

  // ② 미리보기 · 고치기
  const [slides, setSlides] = useState<EditSlide[] | null>(null);
  const [usedGuide, setUsedGuide] = useState('');
  const [sel, setSel] = useState(0);
  const [pdf, setPdf] = useState<{ url: string; blob: Blob } | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [dirty, setDirty] = useState(false);

  // 저장
  const [fileName, setFileName] = useState('');
  const [want, setWant] = useState({ pptx: true, pdf: true });
  const [saving, setSaving] = useState<'' | 'local' | 'drive'>('');
  const [saved, setSaved] = useState<string[]>([]);

  useEffect(() => {
    let stop = false;
    deckApi.guides().then((g) => { if (!stop) { setGuides(g.guides); setGuide((v) => v || (g.guides[0]?.version ?? '')); } }).catch((e) => { if (!stop) setError(e.message); });
    deckApi.layouts().then((l) => { if (!stop) setLayouts(new Map(l.layouts.map((x) => [x.name, x]))); }).catch((e) => { if (!stop) setError(e.message); });
    return () => { stop = true; };
  }, []);
  // AI 가 도는 중이거나 고친 것이 있는데 새로고침 · 탭 닫기를 하면 묻는다 — 서버에 남지 않는다
  useEffect(() => {
    if (!slides && phase !== 'reading' && phase !== 'ai') return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [slides, phase]);
  useEffect(() => () => { if (pdf) URL.revokeObjectURL(pdf.url); }, [pdf]);

  const byImage = useMemo(() => new Map(images.map((f) => [f.name, f])), [images]);
  const busy = phase === 'reading' || phase === 'ai';
  const layoutList = useMemo(() => Array.from(layouts.values()).filter((l) => !l.unused), [layouts]);
  const docTitle = () => title.trim() || projectName;

  /** 화면에서 고친 장 → grk-deck 에 보낼 모양 */
  const payload = useCallback((list: EditSlide[]) => {
    const pics: { slide: number; key: string; file: File }[] = [];
    const out = list.map((s, i) => {
      for (const [key, name] of Object.entries(s.pics)) {
        const f = byImage.get(name);
        if (f) pics.push({ slide: i, key, file: f });
      }
      return { layout: s.layout, texts: s.texts };
    });
    return { out, pics };
  }, [byImage]);

  const renderPdf = useCallback(async (list: EditSlide[]) => {
    setPdfBusy(true); setPdfError('');
    try {
      const { out, pics } = payload(list);
      const blob = await deckApi.preview(docTitle(), out, pics);
      setPdf({ url: URL.createObjectURL(blob), blob });
      setDirty(false);
      return blob;
    } catch (e) {
      setPdfError((e as Error).message);
      return null;
    } finally {
      setPdfBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, title]);

  const openEditor = (list: EditSlide[], guideVer: string) => {
    setSlides(list); setUsedGuide(guideVer); setSel(0); setSaved([]);
    setFileName(safeName(`${docTitle()}_제안서_${today()}`));
    setStep(2);
    renderPdf(list);
  };

  /** AI 구성안 → 고칠 수 있는 장. 메시지는 메시지 칸에, 표지 제목 · 연락처를 채우고, 그림은 올린 것에서 */
  const fromAi = (raw: DraftSlide[]): EditSlide[] => raw.map((r, i) => {
    const l = layouts.get(r.layout);
    const texts = { ...r.texts };
    const msg = l?.slots.find((s) => s.role === 'msg');
    if (msg && r.message.trim() && !texts[msg.key]) texts[msg.key] = r.message;
    const ct = l?.slots.find((s) => s.role === 'contact');
    if (ct && user) texts[ct.key] = `${user.name} | ${user.email}`;
    const ctitle = l?.slots.find((s) => s.role === 'coverTitle');
    if (ctitle && !texts[ctitle.key]) texts[ctitle.key] = docTitle();
    const pic = l?.slots.find((s) => s.role === 'pic');
    const name = i === 0 ? coverPhoto : (r.image.split('/').pop() ?? '');
    return { layout: r.layout, texts, pics: pic && name && byImage.has(name) ? { [pic.key]: name } : {}, source: r.source, unknownNumbers: r.unknownNumbers };
  });

  const start = async () => {
    setError(''); setRead(null); setAiMs(null);
    setPhase('reading');
    try {
      const r = await deckApi.startProjectDraft(projectId, { title: docTitle(), slides: size, cover, guide, images });
      setRead(r.files);
      startedAt.current = Date.now();
      setJobId(r.id);
      setPhase('ai');
    } catch (e) {
      setError((e as Error).message);
      setPhase('failed');
    }
  };

  // AI 구성이 끝날 때까지 본다 → 끝나면 ② 로
  useEffect(() => {
    if (!jobId) return;
    let stop = false;
    const tick = async () => {
      try {
        const s = await deckApi.draftStatus(jobId);
        if (stop) return;
        if (s.files) setRead(s.files);
        if (s.state === 'pending') { setTimeout(tick, POLL_MS); return; }
        setJobId(null);
        if (s.state === 'failed') { setError(s.error); setPhase('failed'); return; }
        setAiMs(Date.now() - startedAt.current);
        setPhase('idle');
        openEditor(fromAi(s.slides), s.guide ?? guide);
      } catch (e) {
        if (!stop) { setError((e as Error).message); setPhase('failed'); setJobId(null); }
      }
    };
    const t = setTimeout(tick, POLL_MS);
    return () => { stop = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // ── ② 고치기
  const edit = (fn: (list: EditSlide[]) => EditSlide[]) => { setSlides((v) => (v ? fn(v) : v)); setDirty(true); setSaved([]); };
  const setText = (key: string, v: string) => edit((list) => list.map((s, i) => (i === sel ? { ...s, texts: { ...s.texts, [key]: v } } : s)));
  const setPic = (key: string, name: string) => edit((list) => list.map((s, i) => {
    if (i !== sel) return s;
    const pics = { ...s.pics };
    if (name) pics[key] = name; else delete pics[key];
    return { ...s, pics };
  }));
  const setLayout = (name: string) => edit((list) => list.map((s, i) => {
    if (i !== sel) return s;
    const a = layouts.get(s.layout)?.slots ?? [];
    const b = layouts.get(name)?.slots ?? [];
    return { ...s, layout: name, texts: moveSlots(a, b, s.texts), pics: moveSlots(a, b, s.pics) };
  }));
  const move = (d: -1 | 1) => {
    const j = sel + d;
    if (!slides || j < 0 || j >= slides.length) return;
    edit((list) => { const n = [...list]; [n[sel], n[j]] = [n[j], n[sel]]; return n; });
    setSel(j);
  };
  const addSlide = (layout: string) => {
    edit((list) => [...list.slice(0, sel + 1), { layout, texts: {}, pics: {}, source: '', unknownNumbers: [] }, ...list.slice(sel + 1)]);
    setSel(sel + 1);
  };
  const duplicate = () => { edit((list) => [...list.slice(0, sel + 1), { ...list[sel], texts: { ...list[sel].texts }, pics: { ...list[sel].pics } }, ...list.slice(sel + 1)]); setSel(sel + 1); };
  const remove = () => {
    if (!slides || slides.length <= 1) return;
    edit((list) => list.filter((_, i) => i !== sel));
    setSel(Math.max(0, Math.min(sel, slides.length - 2)));
  };

  const checks = useMemo(() => ({
    unknown: (slides ?? []).reduce((n, s) => n + leftNumbers(s).length, 0),
    confirm: (slides ?? []).filter((s) => slideText(s).includes(CONFIRM)).length,
  }), [slides]);

  // ── 저장 — 내 컴퓨터(PPTX · PDF) · 드라이브 「02.제안서」(PPTX)
  const saveLocal = async () => {
    if (!slides) return;
    const name = safeName(fileName) || safeName(docTitle());
    setSaving('local'); setError('');
    const done: string[] = [];
    try {
      if (want.pptx) {
        const { out, pics } = payload(slides);
        deckApi.save(await deckApi.build(docTitle(), out, pics), `${name}.pptx`);
        done.push(`${name}.pptx`);
      }
      if (want.pdf) {
        const blob = !dirty && pdf ? pdf.blob : await renderPdf(slides);
        if (!blob) throw new Error('PDF 를 만들지 못했습니다');
        deckApi.save(blob, `${name}.pdf`);
        done.push(`${name}.pdf`);
      }
      setSaved(done);
    } catch (e) {
      setError(`저장하지 못했습니다 — ${(e as Error).message}`);
      if (done.length) setSaved(done);
    } finally {
      setSaving('');
    }
  };
  const saveDrive = async () => {
    if (!slides) return;
    const name = safeName(fileName) || safeName(docTitle());
    setSaving('drive'); setError('');
    try {
      const { out, pics } = payload(slides);
      const blob = await deckApi.build(docTitle(), out, pics);
      const d = await uploadProjectFile(projectId, '제안서', new File([blob], `${name}.pptx`, { type: blob.type }));
      showToast(`드라이브 「02.제안서」에 올리고 아카이브에 등록했습니다 — ${d.fileName}`, 'success');
    } catch (e) {
      setError(`드라이브에 올리지 못했습니다 — ${(e as Error).message}`);
    } finally {
      setSaving('');
    }
  };

  const close = async () => {
    if ((slides || busy) && !(await showConfirm('닫으면 만든 장 · 고친 내용이 사라집니다(서버에 남지 않습니다). 닫을까요?'))) return;
    onClose();
  };

  const readRow = (name: string): DraftFile | undefined => read?.find((r) => r.name === name);
  const cur = slides?.[sel];
  const curLayout = cur ? layouts.get(cur.layout) : undefined;
  const pages = usePdfPages(pdf?.blob ?? null);
  // 빈 칸은 접어 둔다 — 장을 옮기면 다시 접는다
  const [showEmpty, setShowEmpty] = useState(false);
  useEffect(() => { setShowEmpty(false); }, [sel]);
  // AI 구성 중 지난 시간
  const [, setTick] = useState(0);
  useEffect(() => {
    if (phase !== 'ai') return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [phase]);
  const elapsed = phase === 'ai' ? Date.now() - startedAt.current : 0;
  const blockReason = busy ? '' : layouts.size === 0 ? '레이아웃을 불러오는 중입니다'
    : sections.length === 0 ? '글이 있는 제안서 칸이 없습니다 — 제안서를 먼저 씁니다' : !guide ? '가이드 md 가 없습니다' : '';
  const titleOf = (s: EditSlide) => {
    const l = layouts.get(s.layout);
    const k = l?.slots.find((x) => x.role === 'msg' || x.role === 'coverTitle' || x.role === 'toc' || x.role === 'cvName' || x.role === 'chapTitle')?.key;
    return (k && s.texts[k]?.split('\n')[0]) || '';
  };
  const reachable = (n: Step) => n === 1 || !!slides;
  const srcChars = sections.reduce((n, s) => n + (s.content ?? '').length, 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-50">
      <div className="mx-auto max-w-[1400px] p-6 space-y-5">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">PPT 만들기</h1>
            <p className="mt-1 text-sm text-slate-500">제안서 칸을 원고로 AI 가 GRK 가이드 md 대로 초안을 만듭니다. 미리보기에서 고친 뒤 PPTX · PDF 로 저장하세요. 서버에는 남지 않습니다.</p>
          </div>
          <button type="button" onClick={close} aria-label="닫기" className="rounded p-1.5 text-slate-500 hover:bg-slate-200"><X size={18} /></button>
        </header>

        {/* 단계 */}
        <ol className="flex items-center gap-3 text-sm">
          {STEPS.map((label, i) => {
            const n = (i + 1) as Step;
            const on = step === n;
            const done = step > n;
            const ok = reachable(n);
            return (
              <React.Fragment key={label}>
                {i > 0 && <li aria-hidden="true" className={`h-0.5 w-16 rounded-full sm:w-24 ${step >= n ? 'bg-indigo-600' : 'bg-slate-300'}`} />}
                <li>
                  <button type="button" disabled={!ok || busy || on} onClick={() => setStep(n)} aria-current={on ? 'step' : undefined} className="group flex items-center gap-2 disabled:cursor-default">
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${on || done ? 'bg-indigo-600 text-white' : 'border-2 border-slate-300 bg-white text-slate-400'} ${on ? 'ring-4 ring-indigo-100' : ''}`}>
                      {done ? '✓' : n}
                    </span>
                    <span className={`font-medium ${on ? 'text-slate-900' : ok ? 'text-slate-600 group-hover:text-indigo-600' : 'text-slate-400'}`}>{label}</span>
                  </button>
                </li>
              </React.Fragment>
            );
          })}
        </ol>

        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        {/* ① 원고 · 설정 */}
        {step === 1 && (
          <section className="max-w-4xl bg-white rounded-lg shadow border border-slate-200 text-sm">
            <div className="space-y-4 p-5">
              <Row label="템플릿">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-lg bg-indigo-600 px-3.5 py-1.5 text-sm font-medium text-white">GRK</span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-500">
                    가이드 md
                    {guides.length > 1 ? (
                      <select value={guide} onChange={(e) => setGuide(e.target.value)} disabled={busy} className="rounded border border-slate-300 px-1.5 py-0.5 text-xs">
                        {guides.map((g, i) => <option key={g.version} value={g.version}>{g.version}{i === 0 ? ' (최신)' : ''}</option>)}
                      </select>
                    ) : <span className="text-slate-700">{guide || '—'}</span>}
                    {guide && <>· <button type="button" onClick={() => deckApi.downloadGuide(guide).catch((e) => setError(e.message))} className="text-indigo-600 hover:underline">내려받기</button></>}
                  </span>
                </div>
              </Row>

              <Row label="원고" top>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-xs text-slate-500">제안서 칸 {sections.length}개 · {srcChars.toLocaleString()}자 — 제안서 탭에서 고치면 여기에 들어옵니다</p>
                  {sections.length > 0 && (
                    <ul className="mt-1.5 max-h-40 divide-y divide-slate-100 overflow-y-auto">
                      {sections.map((s) => (
                        <li key={s.id} className="flex items-center justify-between gap-3 py-1">
                          <span className="truncate text-slate-700">{s.code} {s.title}</span>
                          <span className="shrink-0 text-xs text-slate-400">{(s.content ?? '').length.toLocaleString()}자</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {read?.find((r) => r.status === '못 읽음') && <p className="mt-1 text-xs text-red-600">못 읽은 파일이 있습니다 — {read.filter((r) => r.status === '못 읽음').map((r) => r.name).join(', ')}</p>}
                </div>
              </Row>

              <Row label="그림" top>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" disabled={busy} onClick={() => imgInput.current?.click()} className="px-3 py-1.5 rounded-lg text-xs border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50">그림 고르기</button>
                  <span className="text-xs text-slate-500">png · jpg — 표지 사진 · 그림 칸 후보. 없어도 됩니다</span>
                  <input ref={imgInput} type="file" multiple hidden accept=".png,.jpg,.jpeg" onChange={(e) => { setImages(Array.from(e.target.files ?? [])); setCoverPhoto(''); e.target.value = ''; }} />
                </div>
                {images.length > 0 && (
                  <ul className="mt-1.5 flex flex-wrap gap-1.5">
                    {images.map((f) => <li key={f.name}><Badge tone={readRow(f.name)?.status === '그림' ? 'green' : 'slate'}>{f.name}</Badge></li>)}
                  </ul>
                )}
              </Row>

              <Row label="제목">
                <input value={title} onChange={(e) => setTitle(e.target.value)} disabled={busy} maxLength={200} placeholder={projectName} className="w-full rounded-lg border border-slate-300 px-3 py-2" />
              </Row>

              <Row label="표지">
                <div className="flex flex-wrap items-center gap-3">
                  <Segmented options={COVERS} value={cover} onChange={setCover} disabled={busy} />
                  <label className="flex items-center gap-2 text-xs text-slate-500">
                    사진
                    <select value={coverPhoto} onChange={(e) => setCoverPhoto(e.target.value)} disabled={busy} className="max-w-[16rem] rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-700">
                      <option value="">기본 사진</option>
                      {images.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
                    </select>
                  </label>
                </div>
              </Row>

              <Row label="분량">
                <Segmented options={SIZES.map((n) => ({ v: n, label: `약 ${n}장` }))} value={size} onChange={setSize} disabled={busy} />
              </Row>
            </div>

            {(busy || aiMs != null) && (
              <div className="mx-5 mb-4 rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className={phase === 'reading' ? 'font-medium text-indigo-700' : 'text-slate-600'}>{phase === 'reading' ? '… 원고 보내는 중' : '✓ 원고 보내기'}</span>
                  <span className="text-slate-300">→</span>
                  <span className={phase === 'ai' ? 'font-medium text-indigo-700' : 'text-slate-600'}>
                    {phase === 'ai' ? `… AI 구성 중 · ${mmss(elapsed)} 지남` : aiMs != null ? `✓ AI 구성 · ${mmss(aiMs)}` : 'AI 구성'}
                  </span>
                </div>
                {phase === 'ai' && (
                  <>
                    <div className="mt-2 h-1 overflow-hidden rounded bg-indigo-100"><div className="h-full rounded bg-indigo-500 transition-all" style={{ width: `${Math.min(95, elapsed / 3000)}%` }} /></div>
                    <p className="mt-1.5 text-xs text-slate-500">보통 3~5분 · 이 화면을 닫거나 새로고침하면 결과를 받지 못합니다</p>
                  </>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-b-lg border-t border-slate-200 bg-slate-50 px-5 py-3">
              <span className="text-xs text-amber-600">{slides && !busy && '다시 만들면 지금 고친 내용이 바뀝니다'}</span>
              <span className="flex items-center gap-3">
                {blockReason && <span className="text-xs text-slate-500">{blockReason}</span>}
                <button type="button" onClick={start} disabled={busy || !!blockReason} className="px-5 py-2 rounded-lg font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                  {busy ? '초안 만드는 중…' : slides ? '초안 다시 만들기 →' : '초안 만들기 →'}
                </button>
              </span>
            </div>
          </section>
        )}

        {/* ② 미리보기(수정) · 저장 */}
        {step === 2 && slides && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg bg-white border border-slate-200 shadow px-4 py-2.5 text-sm">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="truncate font-medium text-slate-800">{docTitle()}</span>
                <span className="text-xs text-slate-400">{slides.length}장 · GRK · 가이드 {usedGuide || '—'} · {COVERS.find((c) => c.v === cover)?.label}</span>
                {checks.unknown > 0 && <Badge tone="amber">원문에 없는 숫자 {checks.unknown}</Badge>}
                {checks.confirm > 0 && <Badge tone="amber">확인 필요 {checks.confirm}</Badge>}
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <input aria-label="파일 이름" title="파일 이름" value={fileName} onChange={(e) => { setFileName(e.target.value); setSaved([]); }} maxLength={150} className="w-56 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm text-slate-800" />
                <div role="group" aria-label="저장할 파일" className="inline-flex overflow-hidden rounded-lg border border-slate-300">
                  {(['pptx', 'pdf'] as const).map((k, i) => (
                    <button key={k} type="button" aria-pressed={want[k]} title={k === 'pdf' ? '미리보기와 같은 모양' : '발표 자료'}
                      onClick={() => { setWant({ ...want, [k]: !want[k] }); setSaved([]); }}
                      className={`px-3 py-1.5 text-xs ${i ? 'border-l border-slate-300' : ''} ${want[k] ? 'bg-indigo-50 font-medium text-indigo-700' : 'bg-white text-slate-400'}`}>
                      {want[k] ? '✓ ' : ''}{k.toUpperCase()}
                    </button>
                  ))}
                </div>
                <button type="button" onClick={saveLocal} disabled={!!saving || !safeName(fileName) || !(want.pptx || want.pdf)} className="px-4 py-1.5 rounded-lg font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                  {saving === 'local' ? '저장하는 중…' : '내 컴퓨터에 저장'}
                </button>
                <button type="button" onClick={saveDrive} disabled={!!saving || !safeName(fileName)} title="PPTX 를 드라이브 사업 폴더 「02.제안서」에 올리고 아카이브에 등록합니다" className="px-4 py-1.5 rounded-lg font-medium border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
                  {saving === 'drive' ? '올리는 중…' : '드라이브에 저장'}
                </button>
              </div>
            </div>

            {saved.length > 0 && (
              <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">
                <span>저장했습니다 — {saved.join(' · ')} (내려받기 폴더). 서버에는 남지 않습니다</span>
                <button type="button" onClick={() => setSaved([])} className="px-3 py-1 rounded-lg border border-green-200 bg-white text-xs">계속 고치기</button>
              </div>
            )}

            {/* 장 썸네일 */}
            <ol aria-label="장 목록" className="flex gap-2 overflow-x-auto pb-1">
              {slides.map((s, i) => {
                const warn = leftNumbers(s).length > 0 || slideText(s).includes(CONFIRM);
                return (
                  <li key={i} className="shrink-0">
                    <button type="button" onClick={() => setSel(i)} aria-current={i === sel ? 'true' : undefined} className={`block w-36 rounded-lg border bg-white p-1.5 text-left ${i === sel ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-200 hover:border-slate-300'}`}>
                      <span className="relative block aspect-video overflow-hidden rounded bg-slate-100">
                        {pages.pages[i]
                          ? <img src={pages.pages[i]} alt="" className="h-full w-full object-cover" />
                          : <span className="flex h-full items-center justify-center px-1 text-center text-[11px] text-slate-400">{s.layout}</span>}
                        {warn && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white" title="확인할 것이 있습니다" />}
                      </span>
                      <span className="mt-1 flex gap-1 text-xs">
                        <span className="text-slate-400">{i + 1}</span>
                        <span className="truncate font-medium text-slate-700" title={titleOf(s) || s.layout}>{titleOf(s) || s.layout}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
              <li className="shrink-0">
                <label className="flex h-full w-36 flex-col justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-white p-2 text-xs text-slate-500">
                  <span className="text-center">+ 장 추가 ({sel + 1}장 뒤)</span>
                  <select value="" onChange={(e) => { if (e.target.value) addSlide(e.target.value); }} className="w-full rounded border border-slate-300 px-1 py-1 text-xs text-slate-700">
                    <option value="">레이아웃 고르기</option>
                    {layoutList.map((l) => <option key={l.name} value={l.name}>{l.name}</option>)}
                  </select>
                </label>
              </li>
            </ol>

            <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,1fr)_22rem]">
              {/* 미리보기 — 장 그림 */}
              <section className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden min-w-0">
                <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-1.5 text-xs text-slate-500">
                  <span>미리보기 · {sel + 1} / {slides.length}{pages.busy && !pdfBusy ? ' · 그리는 중…' : ''}</span>
                  <span className="flex items-center gap-2">
                    {dirty && !pdfBusy && <span className="text-amber-700">고친 내용이 아직 반영 안 됨</span>}
                    <button type="button" onClick={() => renderPdf(slides)} disabled={pdfBusy} className={`rounded-lg border px-2.5 py-1 disabled:opacity-50 ${dirty ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-slate-300 hover:bg-slate-50'}`}>{pdfBusy ? '그리는 중…' : '미리보기 새로고침'}</button>
                  </span>
                </div>
                {(pdfError || pages.error) && <p className="bg-red-50 px-3 py-1.5 text-xs text-red-700">미리보기 실패 — {pdfError || pages.error}</p>}
                <div className="bg-slate-100 p-3">
                  <div className="relative aspect-video w-full overflow-hidden rounded bg-white shadow ring-1 ring-slate-200">
                    {pages.pages[sel]
                      ? <img src={pages.pages[sel]} alt={`${sel + 1}장 미리보기`} className="h-full w-full object-contain" />
                      : <p className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">{pdfBusy || pages.busy ? '미리보기를 그리는 중…' : pdf && sel >= pages.pages.length ? '새 장 — 「미리보기 새로고침」을 누르면 보입니다' : '미리보기가 없습니다'}</p>}
                    {pdfBusy && pages.pages[sel] && <span className="absolute right-2 top-2 rounded bg-white/90 px-2 py-1 text-xs text-slate-500 shadow">다시 그리는 중…</span>}
                  </div>
                </div>
                <p className="px-3 py-1.5 text-xs text-slate-400">실제 PPTX 를 PDF 로 바꾼 모양 · 줄바꿈 · 자간은 PowerPoint 와 조금 다를 수 있음</p>
              </section>

              {/* 고치기 */}
              {cur && (() => {
                const slots = curLayout?.slots ?? [];
                const always = (r: string) => r === 'pic' || r === 'msg' || r === 'coverTitle' || r === 'toc' || r === 'cvName' || r === 'chapTitle';
                const shown = slots.filter((sl) => showEmpty || always(sl.role) || (cur.texts[sl.key] ?? '').trim());
                const hidden = slots.filter((sl) => !shown.includes(sl));
                return (
                  <section className="bg-white rounded-lg shadow border border-slate-200 text-sm">
                    <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-2.5">
                      <h2 className="font-semibold text-slate-800">{sel + 1}장 고치기</h2>
                      <div className="flex gap-1 text-xs">
                        <IconBtn title="위로" onClick={() => move(-1)} disabled={sel === 0}>↑</IconBtn>
                        <IconBtn title="아래로" onClick={() => move(1)} disabled={sel === slides.length - 1}>↓</IconBtn>
                        <IconBtn title="복제" onClick={duplicate}>복제</IconBtn>
                        <IconBtn title="삭제" onClick={remove} disabled={slides.length <= 1}>삭제</IconBtn>
                      </div>
                    </div>
                    <div className="space-y-3 p-4 lg:max-h-[70vh] lg:overflow-y-auto">
                      <label className="block">
                        <span className="text-xs text-slate-500">레이아웃</span>
                        <select value={cur.layout} onChange={(e) => setLayout(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-1.5">
                          {!layouts.has(cur.layout) && <option value={cur.layout}>{cur.layout} (없는 레이아웃)</option>}
                          {layoutList.map((l) => <option key={l.name} value={l.name}>{l.name}</option>)}
                        </select>
                      </label>
                      {cur.source && <p className="text-xs text-slate-500">원문 · {cur.source}</p>}
                      {leftNumbers(cur).length > 0 && <p className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-700">원문에 없는 숫자 — {leftNumbers(cur).join(', ')}</p>}
                      {shown.map((slot) => slot.role === 'pic' ? (
                        <label key={slot.key} className="block">
                          <span className="text-xs text-slate-500">{slot.label}</span>
                          <select value={cur.pics[slot.key] ?? ''} onChange={(e) => setPic(slot.key, e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-1.5">
                            <option value="">{sel === 0 ? '기본 사진' : '없음'}</option>
                            {images.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
                            {cur.pics[slot.key] && !byImage.has(cur.pics[slot.key]) && <option value={cur.pics[slot.key]}>{cur.pics[slot.key]} (파일 없음)</option>}
                          </select>
                        </label>
                      ) : (
                        <SlotText
                          key={`${sel}-${slot.key}`}
                          label={slot.label}
                          value={cur.texts[slot.key] ?? ''}
                          rich={RICH.has(slot.role)}
                          rows={Math.min(10, Math.max(RICH.has(slot.role) || slot.role === 'toc' ? 4 : 2, (cur.texts[slot.key] ?? '').split('\n').length + 1))}
                          onChange={(v) => setText(slot.key, v)}
                        />
                      ))}
                      {hidden.length > 0 && (
                        <button type="button" onClick={() => setShowEmpty(true)} className="w-full rounded-lg border border-dashed border-slate-300 px-2 py-1.5 text-left text-xs text-slate-500 hover:bg-slate-50">
                          빈 칸 {hidden.length}개 더 보기 — <span className="text-slate-400">{hidden.map((h) => h.label).join(' · ')}</span>
                        </button>
                      )}
                    </div>
                  </section>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TONE = {
  green: 'bg-green-100 text-green-700',
  red: 'bg-red-100 text-red-700',
  amber: 'bg-amber-50 text-amber-700',
  slate: 'bg-slate-100 text-slate-600',
  indigo: 'bg-indigo-50 text-indigo-700',
};
const Badge: React.FC<{ tone: keyof typeof TONE; title?: string; children: React.ReactNode }> = ({ tone, title, children }) => (
  <span title={title} className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${TONE[tone]}`}>{children}</span>
);

const IconBtn: React.FC<{ title: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }> = ({ title, onClick, disabled, children }) => (
  <button type="button" title={title} onClick={onClick} disabled={disabled} className="rounded border border-slate-200 px-2 py-0.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40">{children}</button>
);

/** 설정 한 줄 — 왼쪽 이름 · 오른쪽 입력 */
const Row: React.FC<{ label: string; top?: boolean; children: React.ReactNode }> = ({ label, top, children }) => (
  <div className={`grid grid-cols-[5rem_minmax(0,1fr)] gap-4 ${top ? 'items-start' : 'items-center'}`}>
    <span className={`text-slate-500 ${top ? 'pt-1.5' : ''}`}>{label}</span>
    <div className="min-w-0">{children}</div>
  </div>
);

/** 붙은 버튼 묶음 — 하나만 고른다 */
function Segmented<T extends string | number>({ options, value, onChange, disabled }: { options: { v: T; label: string }[]; value: T; onChange: (v: T) => void; disabled?: boolean }) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-slate-300">
      {options.map((o, i) => (
        <button key={String(o.v)} type="button" aria-pressed={value === o.v} disabled={disabled} onClick={() => onChange(o.v)}
          className={`px-3.5 py-1.5 text-sm disabled:cursor-not-allowed ${i ? 'border-l border-slate-300' : ''} ${value === o.v ? 'bg-indigo-600 font-medium text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** 서식 버튼을 붙이는 칸 — 본문류(글머리 · 소제목 · 강조가 먹는 칸) */
const RICH = new Set(['body', 'note', 'profile']);
const LINE_HEAD = /^\s*([-•·]\s+|\d+(-\d+)*\.\s+|[가-하]\.\s+)/;

/** 칸 글. 본문류는 위에 서식 버튼 — 고른 줄의 앞 표시를 바꾸고(글머리 · 하위 · 소제목 · 보통), 고른 글을 **굵게** 감싼다 */
const SlotText: React.FC<{ label: string; value: string; rich: boolean; rows: number; onChange: (v: string) => void }> = ({ label, value, rich, rows, onChange }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const id = useId();
  const apply = (fn: (v: string, a: number, b: number) => { v: string; a: number; b: number }) => {
    const t = ref.current;
    if (!t) return;
    const r = fn(value, t.selectionStart, t.selectionEnd);
    onChange(r.v);
    requestAnimationFrame(() => { t.focus(); t.setSelectionRange(r.a, r.b); });
  };
  const head = (prefix: string) => apply((v, a, b) => {
    const start = v.lastIndexOf('\n', a - 1) + 1;
    const nl = v.indexOf('\n', b);
    const end = nl < 0 ? v.length : nl;
    const block = v.slice(start, end).split('\n').map((l) => (l.trim() ? prefix + l.replace(LINE_HEAD, '') : l)).join('\n');
    return { v: v.slice(0, start) + block + v.slice(end), a: start, b: start + block.length };
  });
  const bold = () => apply((v, a, b) => {
    const picked = v.slice(a, b) || '강조';
    return { v: `${v.slice(0, a)}**${picked}**${v.slice(b)}`, a: a + 2, b: a + 2 + picked.length };
  });
  const confirm = value.includes(CONFIRM);
  return (
    <div>
      <span className="flex items-end justify-between gap-2">
        <label htmlFor={id} className="text-xs text-slate-500">{label}</label>
        {rich && (
          <span className="flex gap-1">
            {([['• 글머리', '- '], ['– 하위', '  - '], ['1. 소제목', '1. '], ['보통', '']] as const).map(([t, p]) => (
              <button key={t} type="button" onClick={() => head(p)} className="rounded border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50">{t}</button>
            ))}
            <button type="button" onClick={bold} className="rounded border border-slate-200 px-1.5 py-0.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50">굵게</button>
          </span>
        )}
      </span>
      <textarea
        id={id}
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className={`mt-1 w-full rounded-lg border px-2.5 py-1.5 leading-relaxed ${confirm ? 'border-amber-400 bg-amber-50/40' : 'border-slate-300'}`}
      />
    </div>
  );
};
